// Opening move: SectionAffordancesPlugin - provides mouse-based insertion affordances
// Big play: Hover over section blocks reveals "Add paragraph above/below" buttons
// Time to tackle the tricky bit: Detect section hover via mousemove to show invisible buttons

import type { JSX } from 'react'
import { useEffect, useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { $getRoot } from 'lexical'
import { $isSectionBlockNode } from '../nodes/SectionBlockNode'
import { ensureParagraphSibling } from './sectionBoundaryHelpers'

interface AffordancePosition {
  sectionKey: string
  position: 'top' | 'bottom'
  x: number
  y: number
}

/**
 * SectionAffordancesPlugin - Mouse-based affordances for inserting paragraphs
 *
 * Main play: Tracks section blocks and renders hover-revealed buttons for insertion
 * Big play: Buttons appear when hovering over sections, not when hovering over invisible buttons
 *
 * Features:
 * - Absolutely positioned buttons that appear on section hover
 * - Scroll-aware positioning that stays synchronized with content
 * - Reuses shared helper for consistent paragraph creation
 * - Accessible (Tab focusable, Enter/Space activated, screen-reader labeled)
 *
 * Reference: .local_docs/lexical/articles/overlay-positioning.md
 */
export default function SectionAffordancesPlugin(): JSX.Element | null {
  const [editor] = useLexicalComposerContext()
  const [affordances, setAffordances] = useState<AffordancePosition[]>([])
  const [hoveredSectionKey, setHoveredSectionKey] = useState<string | null>(null)
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    const rootElement = editor.getRootElement()
    if (!rootElement) return

    // Main play: Calculate affordance positions based on section geometries
    const updateAffordances = () => {
      editor.getEditorState().read(() => {
        const root = $getRoot()
        const sections = root.getChildren().filter($isSectionBlockNode)

        const newAffordances: AffordancePosition[] = []

        // Smart affordance placement: avoid overlapping buttons between adjacent sections
        // Strategy: First section gets "Add Above", last section gets "Add Below"
        // Between sections: single "Add Paragraph" button

        sections.forEach((section, index) => {
          const key = section.getKey()
          const dom = editor.getElementByKey(key)
          if (!dom) return

          const sectionRect = dom.getBoundingClientRect()
          const rootRect = rootElement.getBoundingClientRect()

          const relativeX = sectionRect.left - rootRect.left
          const relativeTopY = sectionRect.top - rootRect.top
          const relativeBottomY = sectionRect.bottom - rootRect.top

          // First section: Add "Add Above" button at top
          if (index === 0) {
            newAffordances.push({
              sectionKey: key,
              position: 'top',
              x: relativeX,
              y: relativeTopY - 28,
            })
          }

          // All sections except last: Add single button at bottom (works for between sections)
          if (index < sections.length - 1) {
            newAffordances.push({
              sectionKey: key,
              position: 'bottom',
              x: relativeX,
              y: relativeBottomY + 4,
            })
          }

          // Last section: Add "Add Below" button at bottom
          if (index === sections.length - 1) {
            newAffordances.push({
              sectionKey: key,
              position: 'bottom',
              x: relativeX,
              y: relativeBottomY + 4,
            })
          }
        })

        setAffordances(newAffordances)
      })
    }

    // Main play: Detect which section the mouse is over
    const handleMouseMove = (e: Event) => {
      const target = e.target as HTMLElement
      const sectionBlock = target.closest('.section-block')
      const affordanceButton = target.closest('.section-affordance-button')

      // Critical UX: Keep buttons visible when mouse is over section OR button
      // Prevents buttons from disappearing while user moves mouse towards them
      if (sectionBlock || affordanceButton) {
        // Clear any pending hide timeout - user is interacting with section/buttons
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current)
          hideTimeoutRef.current = null
        }

        if (sectionBlock) {
          // Mouse is over a section - show its affordances immediately
          editor.getEditorState().read(() => {
            const root = $getRoot()
            const sections = root.getChildren().filter($isSectionBlockNode)

            for (const section of sections) {
              const dom = editor.getElementByKey(section.getKey())
              if (dom === sectionBlock) {
                setHoveredSectionKey(section.getKey())
                return
              }
            }
          })
        }
        // If over button, keep current hover state (don't change hoveredSectionKey)
      } else {
        // Mouse not over section or button - delay hiding to allow mouse travel
        // Big play: 150ms grace period prevents buttons from disappearing during mouse movement
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current)
        }
        hideTimeoutRef.current = setTimeout(() => {
          setHoveredSectionKey(null)
          hideTimeoutRef.current = null
        }, 150)
      }
    }

    // Main play: Update positions on editor changes
    const unregisterUpdateListener = editor.registerUpdateListener(() => {
      updateAffordances()
    })

    // Big play: Recalculate on scroll and resize
    const handleScrollOrResize = () => {
      updateAffordances()
    }

    const editorInner = rootElement.closest('.baseline-editor-container')?.querySelector('.editor-inner')
    const editorContainer = rootElement.closest('.baseline-editor-container')

    // Listen for mouse movement over the editor
    editorContainer?.addEventListener('mousemove', handleMouseMove)
    editorInner?.addEventListener('scroll', handleScrollOrResize)
    window.addEventListener('resize', handleScrollOrResize)

    // Initial calculation
    updateAffordances()

    // Victory lap: Clean up listeners and pending timeouts
    return () => {
      unregisterUpdateListener()
      editorContainer?.removeEventListener('mousemove', handleMouseMove)
      editorInner?.removeEventListener('scroll', handleScrollOrResize)
      window.removeEventListener('resize', handleScrollOrResize)

      // Clean up pending hide timeout
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current)
      }
    }
  }, [editor])

  // Main play: Handle affordance activation (create/focus paragraph)
  const handleAffordanceClick = (sectionKey: string, position: 'top' | 'bottom') => {
    editor.update(() => {
      const root = $getRoot()
      const section = root.getChildren().find((child) => child.getKey() === sectionKey)

      if (!section || !$isSectionBlockNode(section)) return

      // Big play: Use shared helper for consistent paragraph creation
      const targetParagraph = ensureParagraphSibling(
        section,
        position === 'top' ? 'before' : 'after'
      )

      // Victory lap: Focus the paragraph
      targetParagraph.selectStart()
    }, { tag: 'section-affordance-activation' })
  }

  // Handle keyboard activation (Enter/Space on focused button)
  const handleAffordanceKeyDown = (
    event: React.KeyboardEvent,
    sectionKey: string,
    position: 'top' | 'bottom'
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleAffordanceClick(sectionKey, position)
    }
  }

  const rootElement = editor.getRootElement()
  if (!rootElement) return null

  // Big play: Use portal to render overlay in editor-inner container (has position: relative)
  const editorInner = rootElement.closest('.editor-inner') as HTMLElement
  if (!editorInner) return null

  const overlayContent = (
    <div
      className="section-affordances-overlay"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        pointerEvents: 'none', // Allow mouse events to pass through to detect section hover
        width: '100%',
        height: '100%',
      }}
    >
      {affordances.map((affordance) => {
        // Show affordance if its section is hovered
        const isVisible = hoveredSectionKey === affordance.sectionKey

        return (
          <button
            key={`${affordance.sectionKey}-${affordance.position}`}
            type="button"
            className={`section-affordance-button section-affordance-${affordance.position}`}
            data-testid={`section-affordance-${affordance.position}`}
            data-section-key={affordance.sectionKey}
            style={{
              position: 'absolute',
              left: `${affordance.x}px`,
              top: `${affordance.y}px`,
              pointerEvents: isVisible ? 'auto' : 'none', // Enable clicks only when visible
              opacity: isVisible ? 1 : 0,
              transition: 'opacity 0.15s ease',
            }}
            onClick={() => handleAffordanceClick(affordance.sectionKey, affordance.position)}
            onKeyDown={(e) => handleAffordanceKeyDown(e, affordance.sectionKey, affordance.position)}
            tabIndex={0}
            aria-label={
              affordance.position === 'top'
                ? 'Add paragraph above section'
                : 'Add paragraph below section'
            }
          >
            {affordance.position === 'top' ? '+ Add Above' : '+ Add Below'}
          </button>
        )
      })}
    </div>
  )

  // Victory lap: Portal overlay into editor-inner (has position: relative for proper positioning)
  return createPortal(overlayContent, editorInner)
}

// Victory lap: Mouse affordances provide explicit insertion controls
// Writers can hover over section blocks to reveal "Add paragraph" buttons
// Shared helper ensures identical behavior to keyboard escape (ArrowDown/ArrowRight)
